import i18n from '~/modules/i18n';

const { t } = i18n.global;

export function defineStatusOptions(status: object) {
  return Object.keys(status).map(key => ({
    label: t(status[key as keyof typeof status]),
    value: key,
  }));
}

export function selectStatus(obj: object, status: string) {
  try {
    const statusValue = obj[status as keyof typeof obj];

    if (isUndefined(statusValue))
      throw new Error('STATUS_NOT_FOUND');

    return t(statusValue);
  }
  catch (error) {
    console.error(error);
  }
}

export function defineStatusColor(colorObj: object, status: string) {
  return colorObj[status as keyof typeof colorObj];
}

export function prepareDateQueryParams(time: Date[]): object[] | null {
  if (!Array.isArray(time) || time.length < 1)
    return null;

  const [start, end] = time;

  const dateStart = DateTime.fromJSDate(start);
  const dateEnd = end
    ? DateTime.fromJSDate(end)
    : dateStart;

  const result = [
    { key: 'dateStart', value: dateStart.toISODate() },
    { key: 'dateEnd', value: dateEnd.toISODate() },
  ];

  if (result[0].value > result[1].value) {
    return result.map(({ key }) => {
      if (key === 'dateStart')
        return { key, value: result[1].value };

      return { key, value: result[0].value };
    });
  }

  return result;
}

export function preparePaymentDateQueryParams(time: Date[]): object[] | null {
  if (!Array.isArray(time) || time.length < 1)
    return null;

  const [start, end] = time;

  const dateStart = DateTime.fromJSDate(start);
  const dateEnd = end
    ? DateTime.fromJSDate(end)
    : dateStart;

  const result = [
    { key: 'paymentDateStart', value: dateStart.toISODate() },
    { key: 'paymentDateEnd', value: dateEnd.toISODate() },
  ];

  if (result[0].value > result[1].value) {
    return result.map(({ key }) => {
      if (key === 'paymentDateStart')
        return { key, value: result[1].value };

      return { key, value: result[0].value };
    });
  }

  return result;
}

export function checkDateField(date: unknown) {
  return Array.isArray(date) && date.length > 0;
}

export function checkFilteredField(field: unknown) {
  return typeof field === 'string' && !isEmptyString(field);
}

export function checkStatusFieldValue(status: string | number) {
  return String(status).includes('ALL') || status === 0 || !status
    ? undefined
    : status;
}

export function prepareQueryParams(filters: any, pagination: typeof PAGINATION) {
  const { page, pageSize } = pagination;

  const filterParams = Object.keys(filters)
    .reduce((acc: any, key: string) => {
      if (key !== 'date' && key !== 'paymentDate') {
        checkFilteredField(filters[key as keyof typeof filters])
        && (acc[key] = filters[key as keyof typeof filters]);
      }

      if (checkDateField(filters[key as keyof typeof filters])) {
        const dateParams: any = filters.date && prepareDateQueryParams(filters.date);
        Array.isArray(dateParams)
        && (acc = { ...acc, dateStart: dateParams[0].value, dateEnd: dateParams[1].value });
        const paymentDateParams: any = filters.paymentDate && preparePaymentDateQueryParams(filters.paymentDate);
        Array.isArray(paymentDateParams)
        && (acc = { ...acc, paymentDateStart: paymentDateParams[0].value, paymentDateEnd: paymentDateParams[1].value });
      }

      return acc;
    }, {});

  return { page, pageSize, ...filterParams };
}

export function prepareReturn(data: any, fnFactory: (arg?: any) => any, pagination?: typeof PAGINATION, isList = false) {
  if (Array.isArray(data)) {
    return !isList
      ? fnFactory(data[0])
      : prepareListReturn(data, fnFactory, pagination ?? PAGINATION);
  }

  return data instanceof Object
    ? fnFactory(data)
    : fnFactory();
}

function prepareListReturn(data: any, fnFactory: (arg: any[]) => any[], pagination: typeof PAGINATION) {
  return !data.items
    ? fnFactory(data)
    : ({
        orderList: fnFactory(data.items),
        pagination,
      });
}
